import { Capacitor } from '@capacitor/core';

// Stripe publishable key — same as used in Checkout.tsx
const STRIPE_PK = 'pk_live_51SM63aH6Lkyawdmsl86MbyFNIEBf7YgNIu40aBD66chCvPEe143eglNlUOwMRkPbscpvb8pPHycbfQciCKolzb0F006xfbjopl';

// Apple Pay merchant ID — must match what's configured in Xcode / Apple Developer portal
const APPLE_PAY_MERCHANT_ID = 'merchant.exora.club';

let initialized = false;

/**
 * Initialize the Capacitor Stripe plugin.
 * Call once at app startup (only runs on native).
 */
export async function initCapacitorStripe(): Promise<void> {
  if (!Capacitor.isNativePlatform() || initialized) return;

  try {
    const { Stripe } = await import('@capacitor-community/stripe');
    await Stripe.initialize({ publishableKey: STRIPE_PK });
    initialized = true;
    console.log('[CapacitorStripe] Initialized');
  } catch (e) {
    console.warn('[CapacitorStripe] Init failed:', e);
  }
}

/**
 * Present the native Stripe PaymentSheet (card + other methods, NO Apple Pay).
 * Returns true if payment succeeded, false if cancelled.
 * Throws on error.
 */
export async function presentNativePaymentSheet(params: {
  clientSecret: string;
  customerId: string;
  ephemeralKey: string;
  merchantDisplayName?: string;
}): Promise<boolean> {
  const { Stripe, PaymentSheetEventsEnum } = await import('@capacitor-community/stripe');

  await Stripe.createPaymentSheet({
    paymentIntentClientSecret: params.clientSecret,
    customerId: params.customerId,
    customerEphemeralKeySecret: params.ephemeralKey,
    merchantDisplayName: params.merchantDisplayName || 'Hotel (via Exora)',
    // Disable Apple Pay in PaymentSheet — we present it separately via createApplePay
    enableApplePay: false,
    // Google Pay (for future Android support)
    enableGooglePay: true,
    GooglePayIsTesting: false,
    // Style
    style: 'alwaysDark',
    // Return URL for Link and other redirect-based methods
    returnURL: 'exora://stripe-redirect',
    // Skip billing address / name / phone / email collection for fastest checkout
    billingDetailsCollectionConfiguration: {
      name: 'Never',
      phone: 'Never',
      email: 'Never',
      address: 'Never',
    },
  } as any);

  const result = await Stripe.presentPaymentSheet();

  if (result?.paymentResult === PaymentSheetEventsEnum.Completed) {
    return true;
  }

  return false;
}

/**
 * Present native Apple Pay sheet (PassKit compliant).
 * Returns true if payment succeeded, false if cancelled.
 * Throws on error.
 */
export async function presentNativeApplePay(params: {
  clientSecret: string;
  merchantDisplayName: string;
  totalAmount: number;
  currency?: string;
  countryCode?: string;
}): Promise<boolean> {
  const { Stripe, ApplePayEventsEnum } = await import('@capacitor-community/stripe');

  await Stripe.createApplePay({
    paymentIntentClientSecret: params.clientSecret,
    paymentSummaryItems: [{
      label: params.merchantDisplayName,
      amount: params.totalAmount,
    }],
    merchantIdentifier: APPLE_PAY_MERCHANT_ID,
    countryCode: params.countryCode || 'US',
    currency: params.currency || 'USD',
  });

  const result = await Stripe.presentApplePay();

  if (result?.paymentResult === ApplePayEventsEnum.Completed) {
    return true;
  }

  return false;
}

/**
 * Check if Apple Pay is available on this device.
 */
export async function isApplePayAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const { Stripe } = await import('@capacitor-community/stripe');
    // isApplePayAvailable() returns void — it throws if Apple Pay is not available
    await Stripe.isApplePayAvailable();
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if we should use native payment sheet (Capacitor iOS/Android).
 */
export function shouldUseNativePayment(): boolean {
  return Capacitor.isNativePlatform();
}
